import React, { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar } from "@fortawesome/free-solid-svg-icons";
import api from "../../api/http";
import "../../styles/produs/ListRating.css";

const ListRating = ({ productId, onUpdateTotalVotes }) => {
  const [ratingPercentages, setRatingPercentages] = useState([0, 0, 0, 0, 0]);
  const [totalVotes, setTotalVotes] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const intervalRef = useRef(null);

  const normalizeList = (data) => {
    if (Array.isArray(data?.results)) return data.results;
    if (Array.isArray(data)) return data;
    return [];
  };

  const compute = (ratings) => {
    const counts = [0, 0, 0, 0, 0];
    let total = 0;
    for (const r of ratings) {
      const v = Number(r?.rating);
      if (Number.isFinite(v) && v >= 1 && v <= 5) { counts[v - 1] += 1; total += 1; }
    }
    const percentages = counts.map((c) => (total ? (c / total) * 100 : 0));
    return { percentages5to1: percentages.reverse(), total };
  };

  const fetchRatings = async ({ silent = false } = {}) => {
    if (!productId) return;
    if (!silent) { setLoading(true); setError(""); }
    try {
      const res = await api.get(`/produs/ratings-list/${productId}`);
      const list = normalizeList(res.data);
      const { percentages5to1, total } = compute(list);
      setRatingPercentages(percentages5to1);
      setTotalVotes(total);
      if (typeof onUpdateTotalVotes === "function") onUpdateTotalVotes(total);
    } catch (e) {
      setError("Nu am putut încărca rating-urile.");
    } finally {
      setLoading(false);
    }
  };

  const stopInterval = () => { if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; } };
  const startInterval = () => { stopInterval(); intervalRef.current = setInterval(() => fetchRatings({ silent: true }), 5000); };

  useEffect(() => {
    fetchRatings({ silent: false });
    startInterval();
    const onFocus = () => startInterval();
    const onBlur = () => stopInterval();
    window.addEventListener("focus", onFocus);
    window.addEventListener("blur", onBlur);
    return () => { window.removeEventListener("focus", onFocus); window.removeEventListener("blur", onBlur); stopInterval(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const avgText = useMemo(() => {
    const p = ratingPercentages;
    const avg = (5 * p[0] + 4 * p[1] + 3 * p[2] + 2 * p[3] + 1 * p[4]) / 100;
    return Number.isFinite(avg) ? avg.toFixed(1) : "0.0";
  }, [ratingPercentages]);

  if (loading) return (
      <div className="lr-state">
        <div className="lr-spinner" />
        <span>Se încarcă rating-urile…</span>
      </div>
  );

  if (error) return <div className="lr-error">{error}</div>;

  return (
      <div className="lr-root">
        <p className="lr-title">Distribuție voturi</p>
        <ul className="lr-list">
          {ratingPercentages.map((pct, index) => {
            const stars = 5 - index;
            return (
                <li key={stars} className="lr-item">
                  <div className="lr-label">
                    <span className="lr-num">{stars}</span>
                    <FontAwesomeIcon icon={faStar} className="lr-star-icon" />
                  </div>
                  <div className="lr-track">
                    <div
                        className="lr-fill"
                        style={{ width: `${pct}%` }}
                        aria-hidden="true"
                    />
                  </div>
                  <span className="lr-pct">{pct.toFixed(1)}%</span>
                </li>
            );
          })}
        </ul>
      </div>
  );
};

export default ListRating;